/**
 * Video model → max clip duration mapping.
 *
 * Feb 2026 — Option A: script/storyboard/pre-production LLMs need to know
 * how long each shot can actually be rendered, otherwise a 15-second-paced
 * shot gets truncated to 10s by e.g. Gemini Omni Flash. This module resolves
 * the user's currently-selected video model (persisted by the AI Toolkit
 * page in `localStorage.selectedAIModels`) and returns its safe cap.
 *
 * Design notes:
 *   • We NEVER exceed 15s — the storyboard LLM's rules and downstream
 *     panel-timecode maths are built around ≤15s shots.
 *   • Unknown models fall back to 15s (the previous constant) — safest
 *     default when the catalog doesn't publish a limit.
 *   • Both key-substring matching and exact-key lookup are supported so
 *     the same map handles admin-catalog keys (`seedance2-i2v`) and legacy
 *     aliases (`seedance2-ref2video`, `Seedance 2`, `seedance-2`).
 */

// Curated cap table. Values reflect the maximum clip duration each model
// can render as of Feb 2026. When in doubt we chose the lower published
// value so users never get mid-sentence truncation.
export const VIDEO_MODEL_MAX_CLIP_SECONDS = {
    // Google
    'gemini-omni-flash':                10,
    'gemini-3-fast-preview':            10,
    'gemini-3-omni-flash':              10,
    'gemini-2.5-omni-flash':            10,
    'gemini-2.5-flash-omni':            10,
    'gemini-flash-omni':                10,
    'veo-3':                             8,
    'veo-3.1':                           8,
    'veo3':                              8,
    'veo3.1':                            8,

    // ByteDance
    'seedance-2':                       15,
    'seedance2':                        15,
    'seedance2-ref2video':              15,
    'seedance2-i2v':                    15,
    'seedance2-ref2image':              15,

    // OpenAI
    'sora-2':                           10,
    'sora2':                            10,
    'sora':                             10,

    // Kling
    'kling':                            10,
    'kling-2.5-turbo':                  10,
    'kling-2.6':                        10,

    // Pixverse
    'pixverse-ref2video':                8,
    'pixverse':                          8,

    // Alibaba / Wan
    'wan':                               5,
    'wan-2.2':                           5,

    // Lightricks / LTX
    'ltx':                               5,
    'ltx-fast':                          5,

    // Minimax
    'minimax':                           6,
    'minimax-hailuo':                    6,

    // Luma / Hunyuan (best-guess conservative defaults)
    'luma':                              9,
    'luma-dream-machine':                9,
    'hunyuan':                          10,
};

// Feb 2026 — Regex fallback for model-name matching. Catches admin-backend
// naming drifts like 'google/gemini-omni-flash-preview' or
// 'fal-ai/gemini-omni-flash-1080p' that don't exact-match the map above.
// Ordered — the FIRST matching regex wins.
const MODEL_NAME_PATTERNS = [
    { pattern: /gemini.*omni.*flash|omni.*flash.*gemini|gemini.*flash.*omni/i, seconds: 10 },
    { pattern: /seedance/i,                          seconds: 15 },
    { pattern: /pixverse/i,                          seconds:  8 },
    { pattern: /\bveo[-_ .]?3/i,                     seconds:  8 },
    { pattern: /\bsora[-_ .]?2?/i,                   seconds: 10 },
    { pattern: /\bkling[-_ .]?/i,                    seconds: 10 },
    { pattern: /\bwan[-_ .]?/i,                      seconds:  5 },
    { pattern: /\bltx/i,                             seconds:  5 },
    { pattern: /minimax|hailuo/i,                    seconds:  6 },
    { pattern: /luma.*dream|dream.*luma|\bluma\b/i,  seconds:  9 },
    { pattern: /hunyuan/i,                           seconds: 10 },
];

// Hard upper limit — never let a resolved cap exceed this, so the shot
// generation rules stay within the panel-timecode math the LLM was
// trained on.
export const MAX_SHOT_SECONDS = 15;

// Default when we cannot resolve a specific model.
export const DEFAULT_SHOT_SECONDS = 15;

/**
 * Video-generation capability keys used to locate the user's active
 * video model in `localStorage.selectedAIModels`.
 *
 * Feb 2026 — This app renders videos ONLY via the "references-to-video"
 * pipeline. So the shot-duration resolver looks *only* at that capability,
 * ignoring text-to-video / image-to-video / start-end-frame selections.
 *
 * We check both:
 *   1. An explicit alias list (fast path — exact match).
 *   2. A regex fallback that matches any capability key whose name
 *      contains "ref" AND "video" in any order/separator. This catches
 *      admin-backend naming drifts like "Reference → Video", "ref2video",
 *      "Refs2Vid", "references_to_videos" etc. without us having to
 *      enumerate every variant.
 */
const VIDEO_CAP_KEYS = [
    'references-to-video',
    'References to video',
    'references_to_video',
    'reference-to-video',
    'reference_to_video',
    'multi-frame-video',
    'keyframes-to-video',
];

// Feb 2026 (Option B follow-up): admin-backend capability naming drifts
// were causing the resolver to fall back to the 15s default even when
// the user had a valid Gemini Omni Flash pick. This regex catches any
// key containing both "ref" and "video" tokens.
const REFERENCES_TO_VIDEO_RE = /ref.*video|video.*ref/i;

/**
 * Normalise a raw model key/name into the canonical map lookup key.
 * Lower-cases + trims + collapses any run of non-alphanumeric characters
 * to a single hyphen so 'Seedance 2', 'seedance-2', 'seedance_2' and
 * 'seedance2' all map to the same lookup.
 */
const normalise = (raw) => (raw || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Look up a single model's cap. Returns null when unknown.
 */
export const getMaxClipSecondsForModel = (modelKey) => {
    if (!modelKey) return null;
    const n = normalise(modelKey);
    if (!n) return null;
    if (VIDEO_MODEL_MAX_CLIP_SECONDS[n] != null) return VIDEO_MODEL_MAX_CLIP_SECONDS[n];
    // Also try without the hyphen (e.g. 'seedance2' vs 'seedance-2')
    const compact = n.replace(/-/g, '');
    if (VIDEO_MODEL_MAX_CLIP_SECONDS[compact] != null) return VIDEO_MODEL_MAX_CLIP_SECONDS[compact];
    // Substring fall-back — catches admin-catalog keys that suffix
    // resolution / variant tags (e.g. 'gemini-omni-flash-1080p').
    for (const key of Object.keys(VIDEO_MODEL_MAX_CLIP_SECONDS)) {
        if (n.includes(key) || compact.includes(key.replace(/-/g, ''))) {
            return VIDEO_MODEL_MAX_CLIP_SECONDS[key];
        }
    }
    // Regex-family fallback — catches admin-catalog keys that prefix
    // vendor namespaces like 'google/gemini-omni-flash-preview'.
    for (const { pattern, seconds } of MODEL_NAME_PATTERNS) {
        if (pattern.test(modelKey)) return seconds;
    }
    return null;
};

/**
 * Resolve the currently-active shot duration for the ongoing project.
 *
 * Reads `localStorage.selectedAIModels` (populated by AIToolkitSelector),
 * finds the user's picked video-generation model(s), returns the lowest
 * cap. Falls back to DEFAULT_SHOT_SECONDS when nothing is resolvable.
 *
 * The result is clamped between 1 and MAX_SHOT_SECONDS.
 */
export const resolveShotDurationSeconds = () => {
    let selected = {};
    try {
        selected = JSON.parse(localStorage.getItem('selectedAIModels') || '{}') || {};
    } catch { /* noop */ }

    let lowestCap = null;
    let matchedModel = null;
    let matchedCap = null;

    // Pass 1: exact-alias lookup (fast path).
    for (const cap of VIDEO_CAP_KEYS) {
        const pick = selected[cap];
        if (!pick) continue;
        const secs = getMaxClipSecondsForModel(pick);
        if (secs != null && (lowestCap == null || secs < lowestCap)) {
            lowestCap = secs;
            matchedModel = pick;
            matchedCap = cap;
        }
    }

    // Pass 2: regex fallback — any capability whose NAME contains both
    // "ref" and "video" tokens (catches admin-backend naming drift like
    // "ref2video", "Reference → Video", "references_to_videos", etc.).
    // Only runs if pass 1 didn't find anything.
    if (lowestCap == null) {
        for (const cap of Object.keys(selected)) {
            if (!REFERENCES_TO_VIDEO_RE.test(cap)) continue;
            const pick = selected[cap];
            if (!pick) continue;
            const secs = getMaxClipSecondsForModel(pick);
            if (secs != null && (lowestCap == null || secs < lowestCap)) {
                lowestCap = secs;
                matchedModel = pick;
                matchedCap = cap;
            }
        }
    }

    // Diagnostic log — helps users pinpoint capability/model-name drift
    // between frontend and admin backend. Gated to non-production so prod
    // consoles stay clean; when a user reports a "shots came out at 15s"
    // bug they can just switch to preview and re-run to see the trace.
    try {
        if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'production') {
            console.log('🎬 [shot-duration] resolveShotDurationSeconds()', {
                selectedAIModels: selected,
                matchedCapability: matchedCap,
                matchedModel,
                resolvedSeconds: lowestCap ?? DEFAULT_SHOT_SECONDS,
                source: lowestCap == null ? 'default' : 'model',
            });
        }
    } catch { /* noop */ }

    if (lowestCap == null) {
        return {
            seconds: DEFAULT_SHOT_SECONDS,
            source: 'default',
            modelKey: null,
            capability: null,
        };
    }

    const clamped = Math.max(1, Math.min(MAX_SHOT_SECONDS, lowestCap));
    return {
        seconds: clamped,
        source: 'model',
        modelKey: matchedModel,
        capability: matchedCap,
    };
};

/**
 * Feb 2026 — Option B payload block. Builds the `video_generation`
 * object that the frontend attaches to /ai/generate-script-async,
 * /ai/generate-storyboard-async and /ai/generate-preproduction-async.
 *
 * The admin backend can use this to:
 *   • log which model was picked at script-writing time,
 *   • validate our frontend-computed shot duration against its own
 *     model catalog (defence-in-depth),
 *   • do additional model-family-specific prompt tuning (e.g. tell
 *     the LLM to avoid VO-heavy shots for a model with weak lip-sync).
 *
 * Returns null when the user hasn't picked a References-to-video model
 * — callers should NOT attach the field in that case.
 */
export const buildVideoGenerationPayload = () => {
    const r = resolveShotDurationSeconds();
    if (r.source !== 'model' || !r.modelKey) return null;
    return {
        capability: r.capability,             // e.g. 'references-to-video'
        model_key: r.modelKey,                // e.g. 'gemini-omni-flash'
        max_clip_duration_seconds: r.seconds, // e.g. 10
    };
};
